import React, { useEffect, useState } from "react";
import styled from "styled-components/native";
import { Button, Container, Spacer, VDBText } from "@components/atoms";
import { ButtonType, ButtonCategory } from "@components/atoms/Button/types";
import { useTermsAndConditions } from "@apis/termsAndConditions";
import { ScrollView, Platform, BackHandler } from "react-native";
import RenderHtml from "react-native-render-html";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useUpdateUser } from "@apis/user";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTranslation } from "react-i18next";
import Loader from "@components/atoms/Loader";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { css, useTheme } from "styled-components/native";
import { useUserAnalytics } from "../../hooks/useAnalytics";
import { TermsOfUseEventType } from "@utils/analytics.web";
import { useTypographyStore } from "@redux/slices/typographySlice";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

type TermsAndConditionsProps = {
  onMoreScreen?: boolean;
};

const TermsAndConditionsScreen: React.FC<TermsAndConditionsProps> = ({
  onMoreScreen = false,
}) => {
  const OverlayContainer = onMoreScreen ? React.Fragment : LayoutContainer;
  const userInfo = useUserInfo();
  const navigation = useNavigation<NavigationProp<any>>();
  const { deviceWidth: width } = useIsFoldable();
  const [htmlContent, setHtmlContent] = useState<{ html: string }>({
    html: "",
  });
  const { i18n } = useTranslation();
  const termsAndConditionsResponse = useTermsAndConditions();
  const acceptTermsAndConditions = useUpdateUser(userInfo.getUserId());
  const theme = useTheme();
  const { trackWebEvent } = useUserAnalytics();
  const typography = useTypographyStore();
  useEffect(() => {
    trackWebEvent(TermsOfUseEventType.CLICK_TERMS_OF_USE, {});
  }, []);

  useEffect(() => {
    const data: string | undefined =
      termsAndConditionsResponse.data?.data?.terms_of_service?.translations[
        i18n.language
      ]?.body;
    if (data) {
      setHtmlContent({ html: data });
    }
  }, [termsAndConditionsResponse.isSuccess]);

  useEffect(() => {
    if (!onMoreScreen || !theme.isMobile) {
      navigation?.setOptions({
        headerLeft: () => <></>,
      });
      const backHandler = BackHandler.addEventListener(
        "hardwareBackPress",
        () => true
      );
      return () => backHandler.remove();
    }
  }, [onMoreScreen]);

  const agreeClicked = () => {
    acceptTermsAndConditions.mutate({
      accepted_terms: true,
    });
  };

  return (
    <OverlayContainer {...(!onMoreScreen && { backgroundColor: "" })}>
      <ParentContainer onMoreScreen={onMoreScreen}>
        {termsAndConditionsResponse.isLoading || !htmlContent.html ? (
          <Loader />
        ) : termsAndConditionsResponse.isSuccess && htmlContent.html ? (
          <ChildContainer>
            <ScrollView>
              <Container>
                <Spacer size={SpaceType.Big} />
                {Platform.OS === "web" ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      color: theme["core-05"],
                      ...typography["Body-R"],
                    }}
                    dangerouslySetInnerHTML={{ __html: htmlContent.html }}
                  />
                ) : (
                  <RenderHtml
                    contentWidth={width}
                    source={htmlContent}
                    tagsStyles={{
                      body: {
                        ...typography["Body-R"],
                        color: theme["core-05"],
                      },
                    }}
                  />
                )}
              </Container>
            </ScrollView>
            {!onMoreScreen && (
              <BottomContainer>
                <ContainerOverlay>
                  <Button
                    type={ButtonType.primary}
                    category={ButtonCategory.default}
                    title={"Agree"}
                    onPress={agreeClicked}
                    loading={termsAndConditionsResponse?.isLoading}
                    disabled={termsAndConditionsResponse?.isLoading}
                  />
                </ContainerOverlay>
              </BottomContainer>
            )}
          </ChildContainer>
        ) : termsAndConditionsResponse.isError ? (
          <VDBText>Error Fetching Data</VDBText>
        ) : null}
      </ParentContainer>
    </OverlayContainer>
  );
};

const ParentContainer = styled.View<{ onMoreScreen: boolean }>`
  flex: 1;
  flex-basis: auto;
  ${({ onMoreScreen }) =>
    onMoreScreen &&
    css`
      height: 80vh;
    `}
`;

const ChildContainer = styled.View`
  flex: 1;
  flex-basis: auto;
`;

const BottomContainer = styled.View`
  align-items: center;
  margin-horizontal: 16px;
  padding-bottom: 40px;
  padding-top: 16px;
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      max-width: 608px;
    `}
`;

export default TermsAndConditionsScreen;
